import { type FC, type ReactNode } from 'react';

import { Box, Icon, type IconName, IconTile, ListRow, Progress, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';

const listRowStyles = css.raw({
  py: 'space.200',
  transition: 'background-color 0.1s',
  _hover: {
    backgroundColor: 'background.neutral',
  },
});

const descriptionStyles = css.raw({
  gap: 'space.25',
});

const progressStyles = css.raw({
  alignItems: 'flex-end',
  display: { base: 'none', md: 'flex' },
  gap: 'space.100',
  px: 'space.150',
});

const bottomProgressStyles = css.raw({
  display: { base: 'flex', md: 'none' },
  flexDirection: 'column',
  gap: 'space.100',
  mt: 'space.150',
});

type TaskListRowRootProps = {
  to: string;
  children: ReactNode;
};

const TaskListRowRoot: FC<TaskListRowRootProps> = ({ to, children }) => (
  <SambaAutoLink.Unstyled
    to={to}
    css={{ width: '100%', _focusVisible: { outlineStyle: 'auto', outlineOffset: '-2px' } }}
  >
    <ListRow css={listRowStyles}>
      {children}
      <ListRow.TrailingAsset>
        <Icon name="CaretRight" size="small" />
      </ListRow.TrailingAsset>
    </ListRow>
  </SambaAutoLink.Unstyled>
);

TaskListRowRoot.displayName = 'TaskListRow';

type TaskListRowIconProps = {
  name: IconName;
};

const TaskListRowIcon: FC<TaskListRowIconProps> = ({ name }) => (
  <ListRow.LeadingAsset>
    <IconTile name={name} />
  </ListRow.LeadingAsset>
);

TaskListRowIcon.displayName = 'TaskListRow.Icon';

type TaskListRowDescriptionProps = {
  children: ReactNode;
  detail?: ReactNode;
};

const TaskListRowDescription: FC<TaskListRowDescriptionProps> = ({ children, detail }) => (
  <ListRow.LeadingContent css={descriptionStyles}>
    <Text className="underlineOnHover" variant="text100">
      {children}
    </Text>
    {detail != null && (
      <Text variant="text50" color="text.weak">
        {detail}
      </Text>
    )}
  </ListRow.LeadingContent>
);

TaskListRowDescription.displayName = 'TaskListRow.Description';

type TaskListRowProgressProps = {
  value?: number | null;
  description?: string | null;
  dateDescription?: string | null;
};

const TaskListRowProgress: FC<TaskListRowProgressProps> = ({ value, description, dateDescription }) => {
  const progressText = [description, dateDescription].filter(Boolean).join(' • ');
  const progressContent = (
    <>
      {value != null && (
        <Box css={{ width: '200px' }}>
          <Progress className={css({ background: 'background.neutral.active' })} value={value} />
        </Box>
      )}
      {progressText && (
        <Text variant="text50" color="text.weak">
          {progressText}
        </Text>
      )}
    </>
  );

  return (
    <>
      <ListRow.TrailingContent css={progressStyles}>{progressContent}</ListRow.TrailingContent>
      <ListRow.BottomContent css={bottomProgressStyles} alignment="inset">
        {progressContent}
      </ListRow.BottomContent>
    </>
  );
};

TaskListRowProgress.displayName = 'TaskListRow.Progress';

export const TaskListRow = Object.assign(TaskListRowRoot, {
  Icon: TaskListRowIcon,
  Description: TaskListRowDescription,
  Progress: TaskListRowProgress,
});
